import styled, { css } from 'styled-components';
import { ColorsTypes } from '~/ui/theme/colors';
import get from 'lodash/get';

export type Sizes = 'sm' | 'md' | 'lg';

export interface StyledButtonProps {
  size?: Sizes;
  disabled?: boolean;
  iconOnly?: boolean;
  color?: ColorsTypes | 'black';
  customColor?: string;
  variant?: 'contained' | 'outline' | 'text';
  invert?: boolean;
  underline?: boolean;
  rounded?: boolean;
  fullWidth?: boolean;
  width?: string | number;
}

const getWidth = (width?: string | number, fullWidth?: boolean) => {
  if (fullWidth) {
    return css`
      width: 100%;
    `;
  }

  if (width) {
    return css`
      width: 100%;
      max-width: ${typeof width === 'string' ? width : `${width}px;`};
    `;
  }

  return css`
    width: auto;
  `;
};

// Maps legacy static color-scale names to dark-aware semantic token groups.
const semanticGroupMap = {
  primary: 'brand',
  success: 'positive',
  error: 'negative',
  warning: 'alert',
  gray: 'neutral',
};

const colorCSS = (theme, color, variant, custom) => {
  const defaultColor = color || 'primary';
  // Map legacy scale names (primary/success/...) to semantic groups FIRST. The static
  // scales still exist on the theme but lack primary/primaryAlt/overPrimary keys, so
  // selecting them yields `undefined` colors (e.g. a white/transparent button).
  const mapped = semanticGroupMap[defaultColor] || defaultColor;
  const group = theme.colors[mapped]?.primary ? mapped : 'brand';
  const customColor = get(theme.colors, custom) || custom;
  // In the dark palette `primaryAlt` is LIGHTER than `primary`, so the contained base
  // (darkColor) must use `primary` in dark to keep the solid brand pink as the resting
  // state and lighten on hover. Light theme keeps its original primaryAlt base.
  const isDark = theme.name === 'dark';
  const baseColor = customColor || theme.colors[group][isDark ? 'primary' : 'primaryAlt'];
  const hoverColor = customColor || theme.colors[group][isDark ? 'primaryAlt' : 'primary'];
  const darkColor = baseColor;
  const mediumColor = baseColor;
  const normalColor = hoverColor;
  const lightColor = theme.colors[group].overPrimary;

  const variants = {
    contained: css`
      background-color: ${darkColor};
      color: ${lightColor};

      .typography {
        color: ${lightColor};
      }

      &:hover {
        background-color: ${normalColor};
        opacity: ${customColor ? 0.85 : 1};
      }

      &:active {
        background-color: ${mediumColor};
        opacity: 1;
      }

      svg {
        color: ${lightColor};
      }
    `,
    outline: css`
      background-color: transparent;
      color: ${darkColor};
      border: 1px solid ${darkColor};

      .typography {
        color: ${darkColor};
      }

      svg {
        color: ${darkColor};
      }

      &:hover {
        border: 1px solid ${normalColor};
        color: ${normalColor};
        opacity: ${customColor ? 0.85 : 1};

        svg {
          color: ${normalColor};
        }
      }

      &:active {
        border: 1px solid ${mediumColor};
        color: ${mediumColor};
        opacity: 1;

        svg {
          color: ${mediumColor};
        }
      }
    `,
    text: css`
      background-color: transparent;
      color: ${darkColor};

      .typography {
        color: ${darkColor};
      }

      svg {
        color: ${darkColor};
      }

      &:hover {
        color: ${normalColor};
        opacity: ${customColor ? 0.85 : 1};

        svg {
          color: ${normalColor};
        }
      }

      &:active {
        color: ${mediumColor};
        opacity: 1;

        svg {
          color: ${mediumColor};
        }
      }
    `,
  };

  return variants[variant || 'contained'];
};

const disabledCSS = (disabled, variant, customColor) => {
  const defaultCss = css`
    opacity: 0.8;
    pointer-events: none;
    color: ${({ theme }) => (customColor ? theme.colors.element.invert : theme.colors.element.disabled)};

    // svg {
    //   color: ${({ theme }) => (customColor ? theme.colors.element.invert : theme.colors.element.disabled)};
    // }
  `;

  const variants = {
    contained: css`
      background-color: ${({ theme }) => theme.colors.element.disabledBackground};
      color: ${({ theme }) => theme.colors.element.disabled};

      .typography {
        color: ${({ theme }) => theme.colors.element.disabled};
      }

      svg {
        color: ${({ theme }) => theme.colors.element.disabled};
      }
    `,
    outline: css`
      border: 1px solid ${({ theme }) => theme.colors.element.disabled};
      color: ${({ theme }) => theme.colors.element.disabled};

      .typography {
        color: ${({ theme }) => theme.colors.element.disabled};
      }

      svg {
        color: ${({ theme }) => theme.colors.element.disabled};
      }
    `,
    text: css`
      color: ${({ theme }) => theme.colors.element.disabled};

      .typography {
        color: ${({ theme }) => theme.colors.element.disabled};
      }

      svg {
        color: ${({ theme }) => theme.colors.element.disabled};
      }
    `,
  };

  return (
    disabled &&
    css`
      ${defaultCss}
      ${variants[variant || 'contained']}
    `
  );
};

const sizeCSS = (size, iconOnly) => {
  const sizes = {
    sm: css`
      padding: 0 12px;
      height: 36px;
      min-width: 36px;
      svg {
        width: 20px;
        height: 20px;
      }

      ${iconOnly &&
      css`
        max-width: 36px;
        padding: 0;
      `}
    `,
    md: css`
      padding: 0 20px;
      height: 48px;
      min-width: 48px;
      svg {
        width: 24px;
        height: 24px;
      }

      ${iconOnly &&
      css`
        max-width: 48px;
        padding: 0;
      `}
    `,
    lg: css`
      padding: 0 24px;
      height: 56px;
      min-width: 56px;
      svg {
        width: 28px;
        height: 28px;
      }

      ${iconOnly &&
      css`
        max-width: 56px;
        padding: 0;
      `}
    `,
  };

  return sizes[size || 'md'];
};

export const StyledButton = styled.button<StyledButtonProps>`
  border: none;
  border-radius: ${({ rounded }) => (rounded ? 16 : 8)}px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
  outline: 0;

  ${({ theme, size, color, disabled, iconOnly, invert, variant, customColor, width, fullWidth }) => css`
    flex-direction: ${invert ? 'row-reverse' : 'row'};

    ${getWidth(width, fullWidth)}
    ${sizeCSS(size, iconOnly)}
    ${colorCSS(theme, color, variant, customColor)}
    ${disabledCSS(disabled, variant, customColor)}
  `}
`;

export const CircleLoading = styled.div`
  width: 18px;
  height: 18px;

  @keyframes loadingCircle {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  animation: loadingCircle 1s linear infinite;

  svg {
    width: 18px;
    height: 18px;
  }
`;
